import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, publicUrl } from "../../lib/api";
import { fmtFecha } from "../../lib/format";
import { paisNombre, MONTOS, PEP_OPCIONES } from "../../lib/kycData";
import { ORIGEN_FONDOS } from "../kyc/StepDatos";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";

const OF = new Set(["compliance_officer", "owner_oficial"]);
const OW = new Set(["owner", "owner_oficial"]);

export const MediaMarca = ({ f }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/company/kyc/archivos/${f.id}`, { responseType: "blob" }).then((r) => {
      u = URL.createObjectURL(r.data);
      setUrl(u);
    }).catch(() => setUrl(null));
    return () => u && URL.revokeObjectURL(u);
  }, [f.id]);
  if (!url) return <p className="text-xs text-slate-500">{f.tipo}: cargando…</p>;
  const video = f.content_type.startsWith("video/");
  return (
    <figure className="relative" data-testid={`ficha-media-${f.tipo}`}>
      {video ? <video src={url} controls className="w-full rounded" /> : <img src={url} alt={f.tipo} className="w-full rounded" />}
      {video && <span className="absolute top-2 left-2 text-[11px] bg-black/50 text-white px-2 rounded">IntiCheck - uso exclusivo de cumplimiento</span>}
      <figcaption className="text-xs text-slate-500">{f.tipo}</figcaption>
    </figure>
  );
};

const ORIGEN = { mrz: "MRZ", codigo: "Código QR/PDF417", ocr_frente: "Texto del frente", ingreso_manual: "Ingreso manual" };

const OrigenDatos = ({ d }) => {
  if (!d) return null;
  return (
    <div className="border-t pt-2 space-y-1" data-testid="ficha-origen-datos">
      <p><b>Origen de los datos del documento:</b></p>
      <ul className="grid sm:grid-cols-2 gap-x-6 text-xs">
        {Object.entries(d.origen || {}).map(([k, v]) => <li key={k} className={v === "ingreso_manual" ? "text-amber-700 font-semibold" : ""}>{k}: {ORIGEN[v] || v}</li>)}
      </ul>
      {d.observacion && <p className="bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2" data-testid="ficha-observacion-datos"><b>El cliente indicó un error en los datos leídos:</b> {d.observacion}</p>}
    </div>
  );
};

const AVISO_TEL = { desechable: "número desechable", voip: "VoIP", repetido: "repetido en otra verificación", fraude: "riesgo de fraude" };

const TelefonoVerificado = ({ c, rol }) => {
  const t = c.telefono_verificacion;
  if (!t) return null;
  const avisos = Object.keys(AVISO_TEL).filter((k) => t[k]);
  const canal = t.metodo === "whatsapp" ? "WhatsApp" : t.metodo === "empresa" ? "WhatsApp (verificado por la empresa)" : `Teléfono (${t.metodo || "—"})`;
  return (
    <div className="border-t pt-2 space-y-1" data-testid="ficha-telefono-verificado">
      {t.verificado
        ? <p className="text-emerald-700 font-semibold" data-testid="ficha-telefono-ok">{canal} verificado ✓ · {fmtFecha(t.verificado_en)}</p>
        : <p className="text-amber-700 font-semibold" data-testid="ficha-telefono-pendiente">Teléfono no verificado (Didit: {t.estado})</p>}
      {avisos.length > 0 && <p className="text-amber-700 text-xs" data-testid="ficha-telefono-avisos">Avisos: {avisos.map((k) => AVISO_TEL[k]).join(", ")}</p>}
      {rol !== "operator" && c.telefono_distinto_formulario && (
        <p className="bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2" data-testid="ficha-telefono-distinto">
          <b>Revisar:</b> el cliente escribió otro número en el formulario ({c.contacto.telefono_formulario}). Se usa el verificado como oficial.
        </p>
      )}
    </div>
  );
};

export const DatosTab = ({ c, rol }) => {
  const s = c.sesiones?.[0];
  const L = (k, v) => <p><b>{k}:</b> {v ?? "—"}</p>;
  return (
    <div className="space-y-3 text-sm" data-testid="ficha-tab-datos">
      {c.aviso_pep && <p className="bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2" data-testid="ficha-aviso-pep">{c.aviso_pep}</p>}
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {L("Documento", `${c.documento.tipo} ${c.documento.numero} · vence ${c.documento.vencimiento}`)}
        {L("Nacimiento", c.fecha_nacimiento)}
        {L("Nacionalidad", paisNombre(c.nacionalidad))}
        {L("Residencia", paisNombre(c.pais_residencia))}
        {L("Destino de envíos", c.pais_destino ? paisNombre(c.pais_destino) : "—")}
        {L("Ocupación", c.ocupacion)}
        {L("Monto mensual", Object.fromEntries(MONTOS)[c.monto_estimado.rango] || c.monto_estimado.rango)}
        {L("Origen de fondos", c.origen_fondos?.codigo ? (Object.fromEntries(ORIGEN_FONDOS)[c.origen_fondos.codigo] || c.origen_fondos.codigo) + (c.origen_fondos.otro ? `: ${c.origen_fondos.otro}` : "") : null)}
        {L("Contacto", `${c.contacto.telefono} · ${c.contacto.correo}`)}
        {L("Domicilio", `${c.domicilios.chile.calle} ${c.domicilios.chile.numero}, ${c.domicilios.chile.comuna}`)}
        {L("PEP declarado", c.pep.tipo === "no" ? "No" : `${Object.fromEntries(PEP_OPCIONES)[c.pep.tipo] || c.pep.tipo} · ${c.pep.cargo} (${c.pep.institucion}, ${c.pep.pais})`)}
        {L("Actualizado", fmtFecha(c.actualizado_en))}
        {L("Registrado", fmtFecha(c.created_at))}
        {L("Sexo", c.sexo)}
      </div>
      <OrigenDatos d={c.datos_documento} />
      <TelefonoVerificado c={c} rol={rol} />
      {c.verificacion_facial === "videollamada_solicitada" && (
        <p className="bg-blue-50 border border-blue-200 text-blue-900 rounded px-3 py-2 text-sm" data-testid="ficha-videollamada">
          <b>Videollamada solicitada:</b> el cliente eligió no usar verificación facial. Aprueba solo después de verificarlo por videollamada y deja en el motivo una nota con la fecha y lo verificado. La videollamada no se graba ni se guarda.
        </p>
      )}
      {c.ddr && <p className="border-t pt-2"><b>Diligencia reforzada:</b> fondos: {c.ddr.origen_fondos} · patrimonio: {c.ddr.origen_patrimonio} · {c.ddr.oficial} · {fmtFecha(c.ddr.fecha)}</p>}
      {rol === "operator" ? <p className="text-xs text-slate-500">Las imágenes del documento solo las ven el owner y el oficial.</p> : (
        <div className="grid sm:grid-cols-2 gap-4">{(s?.archivos || []).map((f) => <MediaMarca key={f.id} f={f} />)}</div>
      )}
    </div>
  );
};

export const RiesgoTab = ({ c }) => {
  const a = c.riesgo_historial?.[0];
  if (!a) return <p className="text-sm text-slate-500">Sin evaluación de riesgo todavía.</p>;
  return (
    <div className="space-y-3 text-sm" data-testid="ficha-tab-riesgo">
      <p className="text-lg"><b data-testid="ficha-risk-score">{a.puntaje ?? "—"}</b> puntos · nivel <b data-testid="ficha-risk-level">{a.nivel}</b>
        {a.diligencia_reforzada_obligatoria && <span className="ml-2 text-amber-700 font-semibold" data-testid="ficha-ddr-obligatoria">diligencia_reforzada_obligatoria</span>}</p>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500"><th>Factor</th><th>Puntos</th><th>Origen</th><th>Detalle</th></tr></thead>
        <tbody>{a.factores.map((x) => (
          <tr key={x.codigo} className="border-t" data-testid={`risk-factor-${x.codigo}`}>
            <td className="py-1">{x.descripcion}</td><td>+{x.puntos}</td>
            <td className={x.origen === "declaracion" ? "text-amber-700 font-semibold" : ""}>{x.origen_texto}</td><td>{x.detalle}</td>
          </tr>))}
        </tbody>
      </table>
      {!a.factores.length && <p className="text-xs text-slate-500">Sin factores de riesgo.</p>}
      <details className="text-xs"><summary className="cursor-pointer">Historial de evaluaciones</summary>
        {c.riesgo_historial.map((r) => <p key={r.id}>{fmtFecha(r.fecha)} · {r.puntaje ?? "—"} ({r.nivel}) · {r.motivo} · config. puntos v{r.version_configuracion?.riesgo_puntos}</p>)}
      </details>
    </div>
  );
};

export const HistorialTab = ({ c }) => (
  <div className="text-sm space-y-1" data-testid="ficha-tab-historial">
    {c.historial.map((h, i) => <p key={i} className="border-b border-slate-100 pb-1">{fmtFecha(h.fecha)} · <b>{h.tipo}</b> · {h.decision} · {h.nombre} ({h.rol}) · {h.motivo}{h.detalle?.nota ? ` · ${h.detalle.nota}` : ""}</p>)}
    {!c.historial.length && <p className="text-slate-500">Sin decisiones todavía.</p>}
  </div>
);

const post = async (url, body, ok, onDone) => {
  try {
    const r = await api.post(url, body);
    toast.success(ok(r.data));
    onDone();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

export const Acciones = ({ c, rol, onDone }) => {
  const [m, setM] = useState("");
  const [link, setLink] = useState(null);
  const ok = m.trim().length >= 10;
  const go = (decision) => post(`/company/clientes/${c.id}/decision`, { decision, motivo: m }, (d) => {
    if (d.link_path) setLink(publicUrl(d.link_path));
    return d.estado === "pendiente_owner" ? "Aprobación registrada: falta la aprobación del owner" : "Decisión registrada";
  }, onDone);
  return (
    <div className="space-y-2 border-t pt-3" data-testid="ficha-actions">
      {c.aprobacion_pendiente && <p className="text-sm text-amber-700" data-testid="ficha-pending-owner">Aprobado por el oficial ({c.aprobacion_pendiente.oficial}); falta la aprobación del owner por {c.aprobacion_pendiente.razon}.</p>}
      <Textarea placeholder="Motivo (obligatorio, mínimo 10 caracteres)" value={m} onChange={(e) => setM(e.target.value)} data-testid="ficha-motivo" />
      <div className="flex flex-wrap gap-2">
        {OF.has(rol) && <Button disabled={!ok} className="bg-emerald-700 hover:bg-emerald-800" onClick={() => go("aprobar")} data-testid="ficha-approve-button">Aprobar</Button>}
        {OF.has(rol) && !["aprobado", "aprobado_provisional", "bloqueado", "rechazado"].includes(c.estado) && <Button disabled={!ok} variant="outline" onClick={() => go("aprobar_provisional")} data-testid="ficha-approve-provisional-button">Aprobar provisional (con límite)</Button>}
        <Button disabled={!ok} variant="outline" onClick={() => go("pedir_antecedentes")} data-testid="ficha-request-button">{c.estado === "requiere_actualizacion" ? "Enviar link de renovación" : "Pedir antecedentes"}</Button>
        {OF.has(rol) && <Button disabled={!ok} className="bg-rose-700 hover:bg-rose-800" onClick={() => go("rechazar")} data-testid="ficha-reject-button">Rechazar</Button>}
        {OW.has(rol) && c.aprobacion_pendiente && [[true, "Aprobar como alta gerencia"], [false, "Rechazar como alta gerencia"]].map(([a, l]) => (
          <Button key={l} disabled={!ok} className="bg-[#1B2A41]" onClick={() => post(`/company/clientes/${c.id}/aprobacion-owner`, { aprueba: a, motivo: m }, () => "Registrado", onDone)} data-testid={`ficha-owner-${a ? "approve" : "reject"}`}>{l}</Button>
        ))}
        {OF.has(rol) && <Button disabled={!ok} variant="outline" onClick={() => post(`/company/clientes/${c.id}/ocupacion`, { incoherente: !c.ocupacion_incoherente, motivo: m }, () => "Registrado", onDone)} data-testid="ficha-ocupacion-button">{c.ocupacion_incoherente ? "Quitar marca de ocupación incoherente" : "Marcar ocupación incoherente con el monto"}</Button>}
      </div>
      {link && <p className="text-xs break-all" data-testid="ficha-request-link">Enlace para el cliente: {link}</p>}
      {OF.has(rol) && !c.fin_relacion && <Button disabled={!ok} size="sm" variant="outline" onClick={() => post(`/company/clientes/${c.id}/terminar-relacion`, { motivo: m }, () => "Relación terminada; vectores faciales borrados", onDone)} data-testid="ficha-end-relation">Terminar relación</Button>}
      {rol !== "operator" && <Button disabled={!ok} size="sm" variant="outline" className="ml-2" onClick={() => post(`/company/clientes/${c.id}/retirar-consentimiento-facial`, { motivo: m }, () => "Consentimiento retirado; vectores borrados", onDone)} data-testid="ficha-withdraw-consent">Registrar retiro del consentimiento facial</Button>}
      {c.fin_relacion && <p className="text-xs text-slate-600">Relación terminada {fmtFecha(c.fin_relacion)} · conservar hasta {fmtFecha(c.conservar_hasta)}</p>}
    </div>
  );
};

export const DdrForm = ({ c, onDone }) => {
  const [f, setF] = useState({ origen_fondos: "", origen_patrimonio: "" });
  const [file, setFile] = useState(null);
  const [nota, setNota] = useState("");
  const send = async () => {
    const fd = new FormData();
    fd.append("origen_fondos", f.origen_fondos);
    fd.append("origen_patrimonio", f.origen_patrimonio);
    fd.append("nota_oficial", nota);
    if (file) fd.append("respaldo", file);
    try {
      await api.post(`/company/clientes/${c.id}/ddr`, fd);
      toast.success("Diligencia reforzada registrada");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const ok = f.origen_fondos.trim().length >= 10 && f.origen_patrimonio.trim().length >= 10 && (file || nota.trim().length >= 20);
  return (
    <div className="space-y-2" data-testid="ddr-form">
      <Textarea placeholder="Origen de los fondos (mínimo 10 caracteres)" value={f.origen_fondos} onChange={(e) => setF({ ...f, origen_fondos: e.target.value })} data-testid="ddr-fondos" />
      <Textarea placeholder="Origen del patrimonio (mínimo 10 caracteres)" value={f.origen_patrimonio} onChange={(e) => setF({ ...f, origen_patrimonio: e.target.value })} data-testid="ddr-patrimonio" />
      <Input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files[0])} data-testid="ddr-file" />
      <Textarea placeholder="Nota del oficial (mínimo 20 caracteres si no adjuntas respaldo; solo válido cuando el único factor es el país GAFI)" value={nota} onChange={(e) => setNota(e.target.value)} data-testid="ddr-nota" />
      <Button disabled={!ok} onClick={send} data-testid="ddr-submit">Guardar diligencia reforzada</Button>
    </div>
  );
};
